
import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, FormArray, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { finalize } from 'rxjs';
import { AuthService } from '../../../services/auth.service';
import { lessonSlideService } from '../../../services/lesson-slide.service';
import { lessonSlide } from '../../../models/lessonSlide.model';
import { CommonApiService } from '@app/services/common-api.service';
import { extractApiErrorMessage } from '@app/utils/ai-error-prompt';
import { unwrapVioResponseData } from '@app/utils/vio-api-response';


interface SlideTypeOption {
  code: string;
  label: string;
}
@Component({
  selector: 'app-lesson-slide-editor',
  templateUrl: './lesson-slide-editor.component.html',
  styleUrl: './lesson-slide-editor.component.scss'
})

export class LessonSlideEditorComponent implements OnInit {

  slideForm!: FormGroup;
  lessonId!: number;
  userId = this.commonAPI.userInfo.userID;
  slideId!: string;
  slideTitle!: string;
  allowMissingSourcePageRef = false;

  /** SSE / long-running load status */
  slideLoadStatus = '';
  /** Blocks navigation and duplicate AI calls while slide is generating. */
  slideGenerating = false;
  /** Element index currently regenerating an image (-1 = none). */
  regeneratingImageIndex: number | null = null;

  slideTypes: SlideTypeOption[] = [
    { code: 'START', label: 'Start Slide' },
    { code: 'BLCC', label: 'Bullet List with Close Caption' },
    { code: 'IMAGECC', label: 'Image with Close Caption' },
    { code: 'IMAGEBLCC', label: 'Image with Bullet List and Close Caption' },
    { code: 'CONV', label: 'Conversation' },
    { code: 'IMAGECONV', label: 'Image with Conversation' },
    { code: 'TABLE', label: 'Table' },
    { code: 'IMAGECOLCC', label: 'Image with Columnar Close Caption' },
    { code: 'PBL', label: 'Picture and bullets' },
    { code: 'QUIZ', label: 'Quiz' },
    { code: 'PQUIZ', label: 'Quiz with picture' },
    { code: '2COLBLCC', label: 'Two Column Bullet List with Close Caption' }
  ];

  elementTypes = ['TEXT', 'IMAGE', 'VIDEO', 'SHAPE', 'BUTTON'];

  constructor(private fb: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private AuthService: AuthService,
    private lessonSlideService: lessonSlideService,
    private commonAPI: CommonApiService
  ) { }

  ngOnInit(): void {
    // debugger;
    this.slideForm = this.fb.group({
      slideId: [''],
      slideTitle: ['', Validators.required],
      openingNote: [''],
      closingNote: [''],
      slideType: ['', Validators.required],
      slideElements: this.fb.array([])
    });

    // Optional: load existing slide here
    this.lessonId = Number(this.route.snapshot.paramMap.get('lessonid'));
    this.slideId = this.route.snapshot.paramMap.get('slideid') as string;
    this.slideTitle = this.route.snapshot.paramMap.get('slideTitle') as string;
    this.allowMissingSourcePageRef =
      this.route.snapshot.queryParamMap.get('allowMissingSourcePageRef') === '1';
    console.log("Slide Editor init with lesson id " + this.lessonId + " and slide id " + this.slideId + " and " + this.slideTitle);

    this.userId = this.AuthService.getUser();


    this.loadLessonSlide();

  }

  loadLessonSlide() {
    if (this.slideGenerating) return;
    this.slideGenerating = true;
    this.slideLoadStatus = 'Loading slide…';
    void this.lessonSlideService
      .streamGetLessonSlide(
        this.userId,
        this.lessonId,
        this.slideId,
        this.slideTitle,
        (ev) => {
        if (ev.type === 'progress' && ev.message) {
          this.slideLoadStatus = String(ev.message);
        }
        if (ev.type === 'complete' && ev['slide']) {
          const res = ev['slide'] as any;
          console.log('Loaded slide:', res);
          this.slideForm.patchValue({
            slideId: res.slideId,
            slideTitle: res.slideTitle,
            openingNote: res.openingNote,
            closingNote: res.closingNote,
            slideType: res.slideType,
          });
          this.slideElements.clear();
          (res.elements || []).forEach((el: any) => {
            this.slideElements.push(this.createElement(el));
          });
          this.slideLoadStatus = '';
        }
        if (ev.type === 'error') {
          this.slideLoadStatus = '';
          alert(String(ev.message || 'Failed to load slide'));
        }
      },
        { allowMissingSourcePageRef: this.allowMissingSourcePageRef },
      )
      .catch((e: Error) => {
        this.slideLoadStatus = '';
        alert(e?.message || 'Failed to load slide');
      })
      .finally(() => {
        this.slideGenerating = false;
      });
  }

  get slideElements(): FormArray {
    return this.slideForm.get('slideElements') as FormArray;
  }

  createElement(element?: any): FormGroup {
    return this.fb.group({
      elementId: [element?.elementId || ''],
      elementType: [element?.elementType || 'TEXT'],
      bulletFlag: [element?.bulletFlag || 'N'],
      indentLevel: [element?.indentLevel ?? 0],
      imageCaption: [element?.imageCaption || ''],
      content: [element?.content || ''],
      notes: [element?.notes || ''],
      imagePrompt: [element?.imagePrompt ?? null],
    });
  }

  addElement(): void {
    this.slideElements.push(this.createElement());
  }

  insertElement(index: number): void {
    this.slideElements.insert(index + 1, this.createElement());
  }

  deleteElement(index: number): void {
    this.slideElements.removeAt(index);
  }

  moveUp(index: number): void {
    if (index === 0) return;
    const element = this.slideElements.at(index);
    this.slideElements.removeAt(index);
    this.slideElements.insert(index - 1, element);
  }

  moveDown(index: number): void {
    if (index === this.slideElements.length - 1) return;
    const element = this.slideElements.at(index);
    this.slideElements.removeAt(index);
    this.slideElements.insert(index + 1, element);
  }

  isText(index: number): boolean {
    return this.slideElements.at(index).get('elementType')?.value === 'TEXT';
  }

  isImage(index: number): boolean {
    return String(this.slideElements.at(index).get('elementType')?.value || '')
      .trim()
      .toUpperCase() === 'IMAGE';
  }

  resolveImagePromptAt(index: number): string {
    const g = this.slideElements.at(index);
    if (!g) return '';
    const fromPrompt = String(g.get('imagePrompt')?.value ?? '').trim();
    if (fromPrompt) return fromPrompt;
    const fromNotes = String(g.get('notes')?.value ?? '').trim();
    if (fromNotes) return fromNotes;
    const content = String(g.get('content')?.value ?? '').trim();
    if (!content) return '';
    if (/\.(png|jpe?g|webp|gif)$/i.test(content)) return '';
    if (content.includes('/') || content.includes('\\')) return '';
    return content;
  }

  regenerateImageAt(index: number): void {
    if (!this.isImage(index) || this.slideGenerating || this.regeneratingImageIndex != null) {
      return;
    }
    const g = this.slideElements.at(index);
    const prompt = this.resolveImagePromptAt(index);
    if (!prompt) {
      alert('No image prompt found. Put the prompt in Notes (or imagePrompt), then try again.');
      return;
    }
    const elementId = String(g.get('elementId')?.value || '').trim();
    this.regeneratingImageIndex = index;
    this.slideLoadStatus = 'Regenerating image…';
    this.lessonSlideService
      .regenerateSlideImage(this.userId, this.lessonId, this.slideId, {
        elementId: elementId || undefined,
        prompt,
      })
      .pipe(
        finalize(() => {
          this.regeneratingImageIndex = null;
          if (this.slideLoadStatus === 'Regenerating image…') this.slideLoadStatus = '';
        }),
      )
      .subscribe({
        next: (res) => {
          const data = (unwrapVioResponseData(res) ?? res) as any;
          const filename = String(data?.filename || data?.content || '').trim();
          if (!filename) {
            alert('Image regeneration returned no filename.');
            return;
          }
          g.patchValue({
            content: filename,
            imagePrompt: String(data?.imagePrompt || prompt).trim() || prompt,
          });
          this.slideLoadStatus = '';
          alert(`Image regenerated: ${filename}`);
        },
        error: (err) => {
          this.slideLoadStatus = '';
          alert(extractApiErrorMessage(err, 'Image regeneration failed.'));
        },
      });
  }

  autoGrow(event: any) {
    const textarea = event.target;
    textarea.style.height = 'auto';
    textarea.style.height = textarea.scrollHeight + 'px';
  }



  gotoTOC() {
    if (this.slideGenerating) {
      alert('Slide generation is in progress. Please wait until it completes.');
      return;
    }
    this.router.navigate(['/SME/lesson/toc/lessonid', this.lessonId], {
      queryParams: { scrollSlideId: this.slideId },
    });
  }

  saveSlide(): void {
    if (this.slideGenerating) {
      alert('Slide generation is in progress. Please wait before saving.');
      return;
    }
    // debugger;
    //if (this.slideForm.invalid) {
    //  this.slideForm.markAllAsTouched();
    //  return;
    //}

    const updatedSlide = this.slideForm.value;
    console.log('Saved Slide:', updatedSlide);

    this.lessonSlideService.saveLessonSlide(this.userId, this.lessonId, this.slideId, updatedSlide)
      .subscribe(() => alert('Saved'));

    // Call API here
  }
}
